import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Button, CircularProgress, Stack, Tooltip } from "@mui/joy";
import Box from "@mui/joy/Box";
import { useMutationState } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { HTTPError } from "griptonite-api-client/client";
import { ApiTaskUpdatePayload } from "griptonite-api-client/types/tasks";
import omit from "lodash/omit";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm, useFormContext, useFormState } from "react-hook-form";

import { Task } from "@/core/types";
import { useCurrentVenueCompetitionLookup } from "@/hooks/routes";
import { useTaskQuery, useUpdateTaskMutation } from "@/hooks/tasks";
import { transformValidationErrorToErrorsArray } from "@/utils/handleFormError";

import FormErrorAlert from "../FormErrorAlert";
import Sidebar from "../Sidebar";
import { useTaskDeleteAction } from "../TaskCard/useTaskDeleteAction";
import { TaskFormValues, TaskFormValuesSchema, taskToFormValues } from "../TaskForm/formValues";
import TaskForm from "../TaskForm/TaskForm";
import { useTaskSetOverview } from "../TaskSetOverview/useTaskSetOverview";
import { CloseButton } from "./CloseButton";

const FormButtons = ({ task }: { task?: Task }) => {
  const formState = useFormState<TaskFormValues>();
  useFormContext();
  const navigate = useNavigate();

  let saveText = "Save";
  if (formState.isSubmitting) {
    saveText = "Saving...";
  }

  const deleteAction = useTaskDeleteAction(task, {
    onComplete: () => {
      if (!task) {
        return;
      }
      navigate({
        to: "/gym/$venueUuid/schedule/set/$setUuid",
        params: { venueUuid: task.venue, setUuid: task.set },
        replace: true,
      });
    },
  });

  return (
    <Stack
      sx={{
        position: "sticky",
        bottom: "0px",
        padding: 1,
        zIndex: 10,
        backgroundColor: "white",
      }}
      direction="column"
      spacing={1}
    >
      {formState.isDirty ? (
        <Button
          type="submit"
          loading={formState.isSubmitting}
          loadingPosition="start"
          color="primary"
          variant="solid"
        >
          {saveText}
        </Button>
      ) : null}
      <Tooltip title={deleteAction.tooltip ?? ""} disableHoverListener={!deleteAction.tooltip}>
        {/* A disabled button emits no pointer events, so the tooltip needs a
            wrapper to hang off. */}
        <Box>
          <Button
            color="danger"
            variant="plain"
            onClick={deleteAction.onClick}
            disabled={deleteAction.disabled}
            loading={deleteAction.isPending}
            loadingPosition="start"
            sx={{ width: "100%" }}
          >
            {deleteAction.isPending ? "Deleting..." : deleteAction.label}
          </Button>
        </Box>
      </Tooltip>
    </Stack>
  );
};

export default function TaskDetailSidebar({ uuid }: { uuid?: string }) {
  const { taskSet } = useTaskSetOverview();

  const { data: task } = useTaskQuery(uuid, taskSet?.uuid);
  const updateMutation = useUpdateTaskMutation(task?.venue);

  const defaultValues = useMemo(() => {
    return taskToFormValues(task, taskSet);
  }, [task, taskSet]);

  const methods = useForm<TaskFormValues>({
    defaultValues,
    resolver: zodResolver(TaskFormValuesSchema),
    mode: "onBlur",
  });

  useEffect(() => {
    methods.reset(taskToFormValues(task, taskSet));
  }, [task, taskSet, methods]);

  const navigate = useNavigate();
  const competitionUuid = methods.watch("route.competition");
  const competition = useCurrentVenueCompetitionLookup(competitionUuid ?? undefined);

  const onSubmit = async (values: TaskFormValues) => {
    const payload: ApiTaskUpdatePayload = {
      ...omit(values, ["routeUuid", "route"]),
      route: omit(values.route, ["competition"]),
      notes: values.notes ?? undefined,
    };
    if (!task) {
      return;
    }
    if (!task.route) {
      payload.route = undefined;
      if (values.routeUuid) {
        payload.route = values.routeUuid;
      }
    } else {
      if (task.type === "SET") {
        payload.assignees = undefined;
        const validRouteGroupUuids = values.route?.competitionRouteGroups.filter((uuid) =>
          competition?.routeGroups.some((crg) => crg.uuid === uuid),
        );
        if (values.route?.competition && payload.route && typeof payload.route === "object") {
          payload.route.competitionRouteGroups = validRouteGroupUuids?.length
            ? validRouteGroupUuids
            : competition?.routeGroups.map((x) => x.uuid);
        } else if (payload.route && typeof payload.route === "object") {
          payload.route.competitionRouteGroups = [];
        }
      }
    }
    if (!uuid) {
      throw new Error("uuid must be set");
    }
    await updateMutation.mutateAsync(
      { uuid, ...payload },
      {
        onError: (error) => {
          if (error instanceof HTTPError && error.response.status === 400 && error.jsonData) {
            const errors = transformValidationErrorToErrorsArray(error);
            console.log({ errors });
            errors.forEach((error) => {
              // oxlint-disable-next-line typescript/no-explicit-any
              methods.setError(error.path as any, {
                type: error.code,
                message: error.message,
              });
            });
          }
        },
        onSuccess: () => {
          navigate({
            to: "/gym/$venueUuid/schedule/set/$setUuid",
            params: { venueUuid: task.venue, setUuid: task.set },
          });
        },
      },
    );
  };

  const createMutationStatus = useMutationState<string>({
    filters: {
      mutationKey: ["createTask"],
      predicate: (mutation) => {
        const variables = mutation.state.variables as { uuid: string } | undefined;
        return variables?.uuid === uuid;
      },
    },
    select: (mutation) => mutation.state.status,
  })[0];

  if (createMutationStatus && createMutationStatus !== "success") {
    return (
      <Sidebar
        sx={{
          borderTopLeftRadius: 16,
          borderLeft: "1px solid",
          borderLeftColor: "neutral.200",
        }}
      >
        <CloseButton />
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {createMutationStatus !== "error" ? (
            <CircularProgress />
          ) : (
            <Alert color="danger">Error creating task. Please try again</Alert>
          )}
        </Box>
      </Sidebar>
    );
  }

  return (
    <Sidebar
      sx={{
        borderTopLeftRadius: 16,
        borderLeft: "1px solid",
        borderLeftColor: "neutral.200",
      }}
    >
      <FormProvider {...methods}>
        <Box
          sx={{ flex: 1, overflow: "auto" }}
          component="form"
          autoComplete="off"
          onSubmit={methods.handleSubmit(onSubmit)}
          onReset={() => methods.reset()}
        >
          <FormErrorAlert />
          <TaskForm task={task} />
          <FormButtons task={task} />
        </Box>
      </FormProvider>
    </Sidebar>
  );
}
